import _ from 'lodash';
import {
  barSize,
  CHART_CONFIG,
  CustomizedXTick,
  CustomizedYTick
} from '../customCharts';
import {
  convertMinutesToDisplayableTime,
  TIME_UNITS
} from 'common/utils/time.utils';
import { calculatePercentage } from 'common/utils/utils';
import { MAX_NUM_OF_SUMMARY_TYPES } from '../SummaryItemBox';
import Text, { Inline } from 'components/base/Text';
import { SPACES } from 'common/constants/sizes';
import { METRICS } from 'common/constants/constants';
import { getColor, getHighlightColors } from 'common/constants/_colors';
import { CustomLineTooltip } from '../tooltips';
import {
  getFormattedIssueTypeStats,
  convertMinutesToTotalDisplay,
  getAllIterationDurationPerIssueType,
  getApiAdditionalFilters,
  formatBaseData
} from './common';

const InvestmentProfileService = investmentProfileIterations => () => {
  // Formatters
  const formatData = rawData => {
    const formattedData = formatBaseData(rawData).map(iteration => {
      const formattedIssueTypeStats = getFormattedIssueTypeStats(
        iteration.stats.provider_type_stats,
        iteration
      );
      return {
        ...iteration,
        stats: {
          issue_type_stats: formattedIssueTypeStats,
          issue_type_stats_by_type: _.keyBy(
            formattedIssueTypeStats,
            'issue_type'
          )
        }
      };
    });
    return _(formattedData).sortBy('start', 'asc').value();
  };

  // Getters
  const getChartConfig = (slicedIterations, issues, chartBarsDef) => {
    return {
      ...CHART_CONFIG,
      xAxis: {
        dataKey: 'name',
        height: 50,
        tick: <CustomizedXTick slicedIterations={slicedIterations} />,
        label: {
          position: 'insideBottom'
        }
      },
      yAxis: {
        tick: <CustomizedYTick />,
        label: {
          value: 'Days',
          offset: 5,
          angle: -90,
          position: 'insideLeft'
        }
      },
      bars: chartBarsDef.filter(
        ({ dataKey }) => issues === 'all' || dataKey === issues
      )
    };
  };

  const getTableConfig = (totalDurationPerIssueType, chartBarsDef) => {
    const totalDays = totalDurationPerIssueType.total || 0;
    let count = 0;

    const data = [
      { name: 'Iteration', dataPoint: 'name' },
      ...chartBarsDef
    ].map(({ name, dataPoint, color }) => {
      const total = _.get(totalDurationPerIssueType, `${name}.total`, 0);
      const totalInUnits = convertMinutesToTotalDisplay(
        convertMinutesToDisplayableTime({
          minutes: total,
          maxTimeUnit: TIME_UNITS.days,
          numOfDecimal: 0
        })
      );
      const dynamicStates = calculatePercentage(total, totalDays, false);

      return {
        name: name.toUpperCase(),
        total,
        order: dataPoint === 'name' ? 0 : 1,
        convertedTotalDays: totalInUnits,
        percents:
          dynamicStates === 0 ? dynamicStates : +dynamicStates.toFixed(1),
        key: name,
        dataPoint: dataPoint || name,
        color
      };
    });

    const orderedData = _.orderBy(data, ['order', 'total'], ['asc', 'desc']);

    const orderedDataWithMore = orderedData.map(item => {
      if (item.dataPoint !== 'name') count++;
      return {
        ...item,
        count,
        isMoreIssueType: count > MAX_NUM_OF_SUMMARY_TYPES
      };
    });

    return orderedDataWithMore;
  };

  const getTableData = iterations => {
    const uniqueFields = _.chain(iterations)
      .map(iteration => [
        iteration.name,
        ...iteration.stats.issue_type_stats.map(({ issue_type }) => issue_type)
      ])
      .flatten()
      .uniq()
      .reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, {})
      .value();

    const data = _.chain(iterations)
      .orderBy('start', 'desc')
      .map(iteration => ({
        ...iteration,
        totalInProgressStateDurationPerIssueType:
          iteration.stats.issue_type_stats_by_type
      }))
      .map(
        ({
          name,
          iterationName,
          iterationDates,
          totalInProgressStateDurationPerIssueType
        }) => {
          return _.reduce(
            {
              ...uniqueFields,
              name,
              ...totalInProgressStateDurationPerIssueType
            },
            (acc, val, key) => {
              if (key === 'name') {
                acc[key] = (
                  <>
                    <Text size='medium' weight='500' pb={SPACES['x-small']}>
                      {iterationName}
                    </Text>
                    <Text size='medium'>{iterationDates}</Text>
                  </>
                );

                return acc;
              }

              const percents =
                val && val.percents ? `${_.round(val.percents, 1)}%` : null;
              const total = val && val.totalInUnits ? val.totalInUnits : null;
              const totalDisplay = convertMinutesToTotalDisplay(total);

              acc[key] = (
                <>
                  {totalDisplay ? (
                    <Inline pr='0.5rem' size='medium' weight='500'>
                      {totalDisplay}
                    </Inline>
                  ) : null}
                  {percents ? (
                    <Inline size='medium' color='shuttleGray'>
                      ({percents})
                    </Inline>
                  ) : null}
                </>
              );

              return acc;
            },
            {}
          );
        }
      )
      .value();

    return data;
  };

  const getDynamicStates = iterations => {
    const relevantCols = _(
      getAllIterationDurationPerIssueType(iterations)
    ).reduce((acc, val, key) => {
      if (val.total > 0) {
        acc.push(key);
      }
      return acc;
    }, []);
    return _.chain(iterations)
      .map(iteration =>
        iteration.stats.issue_type_stats.map(({ issue_type }) => issue_type)
      )
      .flatten()
      .uniq()
      .filter(item => relevantCols.includes(item))
      .value();
  };

  const getChartBarsDef = iterations => {
    const dynamicStates = getDynamicStates(iterations);

    const CHART_BARS_DEF_WITHOUT_BUG = dynamicStates
      .filter(s => s !== METRICS.BUG_STATE)
      .map((state, idx) => {
        const colorNames = getHighlightColors();
        const colorName = colorNames.filter(c => c !== METRICS.BUG_COLOR)[idx];

        return {
          stackId: 'dynamicStates',
          dataKey: `stats.issue_type_stats_by_type[${state}].total`,
          name: state,
          fill: getColor(colorName),
          color: colorName,
          barSize
        };
      });

    const CHART_BARS_DEF_BUG = dynamicStates
      .filter(s => s === METRICS.BUG_STATE)
      .map(state => {
        return {
          stackId: 'dynamicStates',
          dataKey: `stats.issue_type_stats_by_type[${METRICS.BUG_STATE}].total`,
          name: state,
          fill: getColor(METRICS.BUG_COLOR),
          color: METRICS.BUG_COLOR,
          barSize
        };
      });
    return CHART_BARS_DEF_WITHOUT_BUG.concat(CHART_BARS_DEF_BUG);
  };

  const getFilterByIssuesParams = chartBarsDef => {
    const issueTypes = [
      { value: 'all', displayValue: 'All Issues' },
      ...chartBarsDef.map(({ name, dataKey }) => {
        return {
          value: dataKey,
          displayValue: name
        };
      })
    ];

    return {
      placeholder: 'All Issues',
      options: issueTypes
    };
  };

  const getFilterByIterationsParam = () => {
    const timeFrameOptions = investmentProfileIterations.map(timeFrame => ({
      value: timeFrame,
      displayValue: `Last ${timeFrame} iterations`
    }));

    return {
      placeholder: 'Last 5 iterations',
      options: timeFrameOptions
    };
  };

  return {
    formatData,
    convertMinutesToTotalDisplay,
    getChartConfig,
    getTableConfig,
    getTableData,
    getChartBarsDef,
    getDynamicStates,
    graphTitle: 'Investment per issue type over time',
    customLineTootip: (
      <CustomLineTooltip
        convertMinutesToTotalDisplay={convertMinutesToTotalDisplay}
      />
    ),
    getFilterByIssuesParams,
    getFilterByIterationsParam,
    getApiAdditionalFilters
  };
};

export default InvestmentProfileService;
